import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './contexts/AuthContext';
import { useApp } from './contexts/AppContext';
import { useInstitutionConfig } from './contexts/InstitutionConfigContext';
import { ChatbotProvider, useChatbot } from './contexts/ChatbotContext';
import { useMobile } from './contexts/MobileContext';

// App shell
import AppProviders from './AppProviders';
import AppRoutes from './AppRoutes';

// Error handling components
import ErrorBoundary, { SectionErrorBoundary } from './components/shared/ErrorBoundary';
import ErrorState from './components/shared/ErrorState';

// Auth components
import SupabaseAuth from './components/Auth/SupabaseAuth';
import NeedsProvisioning from './components/Auth/NeedsProvisioning';
import RequireTerms from './components/Auth/RequireTerms';
import OnboardingModal, { useOnboarding } from './components/Auth/OnboardingModal';

// Main components
import CommandPalette from './components/CommandPalette';
import StudentCommandBar from './components/StudentCommandBar';

// Column components
import { DegreeAuditColumn } from './components/columns/DegreeAuditColumn';
import { CoursePlannerColumn } from './components/columns/CoursePlannerColumn';
import { ChatColumn } from './components/columns/ChatColumn';

// Core components
import { ModalManager } from './core/ModalManager';

// Shared components
import AvailabilityWarning from './components/shared/AvailabilityWarning';
import Header from './components/Layout/Header';
import PrintHeader from './components/Layout/PrintHeader';
import { MobileTabBar } from './components/Layout/MobileTabBar';
import ChatMinimizedPill from './components/Chatbot/ChatMinimizedPill';
import ToastNotification from './components/shared/ToastNotification';
import { LoadingScreen } from './components/shared/LoadingScreen';
import AppLoadingShell from './components/shared/AppLoadingShell';
import { APCreditsModal } from './components/shared/modals';
import StaleSuggestionsModal from './components/shared/modals/StaleSuggestionsModal';

// Custom hooks
import { useAcademicPlan } from './hooks/useAcademicPlan';
import { useModals } from './hooks/useModals';
import { useUIState } from './hooks/useUIState';
import { useResizableColumns } from './hooks/useResizableColumns';
import { useCourseData, type NextOffering } from './hooks/useCourseData';
import { useToast } from './hooks/useToast';
import { useAcademicPlans } from './hooks/useAcademicPlans';
import { usePlanChangeHandlers } from './hooks/usePlanChangeHandlers';
import { useOptimizerActions } from './hooks/useOptimizerActions';
import { useAppModals } from './hooks/useAppModals';
import { useCommandExecution } from './hooks/useCommandExecution';
import { useGlobalShortcuts } from './hooks/useGlobalShortcuts';
import { useUserUpdate } from './hooks/useUserUpdate';
import { useStaleSuggestionsPrompt } from './hooks/useStaleSuggestionsPrompt';
import { useGradeUpdate } from './hooks/useGradeUpdate';

// Plan management
import { PlanSwitcher } from './components/PlanSwitcher';

// Provider contexts that wrap the planner subtree
import {
  PlannerDndProvider,
  type PlanOpsForDrag,
} from './contexts/DragDropContext';
import {
  SuggestionProvider,
  DEFAULT_OPTIMIZER_MODE,
  OPTIMIZER_LABELS,
  OPTIMIZER_ROTATION,
  type OptimizerMode,
  type SuggestionContextValue,
} from './contexts/SuggestionContext';
import {
  PlanOperationsProvider,
  type PlanOperationsContextValue,
} from './contexts/PlanOperationsContext';
import {
  ModalOperationsProvider,
  type ModalOperationsContextValue,
} from './contexts/ModalOperationsContext';

// Type-only imports
import type { AcademicPlan, EnrolledCourse } from './types';
import type { SemesterSelectionCourse } from './hooks/useModals';

// =============================================================================
// LOCAL TYPES
// =============================================================================

/**
 * Active availability warning shown when a course can't be offered in the
 * targeted semester. Set by checkCourseAvailability, cleared by the dialog.
 */
interface AvailabilityWarningState {
  courseCode: string;
  courseTitle?: string;
  targetSemester: string;
  nextOffering: NextOffering | null;
}

/**
 * Fallback identity used when the supabase user is unexpectedly absent.
 * Includes name/degree/year for the print header — fields the real
 * supabase User type doesn't carry.
 */
interface SafeUser {
  id: string;
  name?: string;
  email?: string;
  degree?: string;
  year?: string;
}

/** Single-source fallback when academicPlan hasn't loaded yet. */
const createFallbackPlan = (): AcademicPlan => ({
  id: 0,
  plan_name: 'My Academic Plan',
  is_active: false,
  semesters: {},
  bookmarkedCourses: [],
  majors: [],
  minors: [],
  citations: [],
});

// =============================================================================
// COMPONENTS
// =============================================================================

function AppContent() {
  const { user, loading: authLoading, signOut, signInWithGoogle } = useAuth();
  const { needsProvisioning, provisioningRefusal, retrySync } = useApp();

  if (authLoading) {
    return <LoadingScreen message="Loading authentication..." />;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-surface-ground flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <SupabaseAuth />
      </div>
    );
  }

  // Authenticated but no account yet: say why (open-access plan §3.9) instead
  // of showing the main app, which would break with no dbUser.
  if (needsProvisioning) {
    return (
      <NeedsProvisioning
        email={user.email ?? ''}
        reason={provisioningRefusal?.reason ?? 'no_grant'}
        eligibleDomains={provisioningRefusal?.eligibleDomains ?? []}
        onSignOut={signOut}
        onSwitchAccount={async () => {
          await signOut();
          await signInWithGoogle();
        }}
        onRetry={retrySync}
      />
    );
  }

  // Provisioned but hasn't accepted the current terms (open-access plan §3.3):
  // the backend refuses AI calls until they do, so the whole app waits behind
  // the gate. /settings and /admin/* use the same wrapper (AppRoutes.tsx).
  return (
    <RequireTerms>
      <ChatbotProvider>
        <AuthenticatedApp />
      </ChatbotProvider>
    </RequireTerms>
  );
}

/**
 * The signed-in application. It reads everything it needs from the same hooks
 * AppContent used to call on its behalf — before F3 these arrived as 14 props
 * whose only purpose was to be forwarded one level down.
 */
function AuthenticatedApp() {
  const { user } = useAuth();
  const { dbUser, setDbUser, syncingUser, setSyncingUser, isAdmin } = useApp();
  const { toasts, showSuccess, showWarning, showError, hideToast } = useToast();
  const { plans, loading: plansLoading, setActivePlan } = useAcademicPlans();

  const activePlan = plans.find((p) => p.is_active) || plans[0];
  const { visibleColumns, showTabBar, isDesktop, isTablet } = useMobile();
  // UX-R1 3.1: when the assistant is disabled on desktop, the chat column
  // collapses (grid drops to 2 tracks) and a floating pill takes its place.
  // The column is hidden, NOT unmounted — ChatInterface keeps its
  // askChatbotHelp listener + message state alive for the pill's quick
  // actions and for instant restore on enable. Tablet/mobile keep the
  // inline disabled card (tab layouts don't have a dead third column).
  const { isEnabled: chatbotEnabled } = useChatbot();
  const chatCollapsed = isDesktop && !chatbotEnabled;

  // Core hooks
  const {
    academicPlan,
    courseRequirements,
    planOps: originalPlanOps,
    planUtils,
    loading: planLoading,
    refreshing: planRefreshing,
    error: planError,
    saving,
    currentPlanId,
  } = useAcademicPlan(activePlan?.id || null);

  // Onboarding - checks 4 flags in user preferences to determine if modal should show
  const { showOnboarding, onboardingMode, completeOnboarding, dismissOnboarding } = useOnboarding();

  const { modals, modalData, modalOps } = useModals();
  const { collapsedSemesters, bookmarkedCollapsed, showBookmarkedCourses, openDropdownId, uiOps } = useUIState(academicPlan);
  const { courses, courseOps } = useCourseData();

  // Local state
  const [chatDebugInfo, setChatDebugInfo] = useState<unknown>(null);
  const [showTransferCredits, setShowTransferCredits] = useState(true);
  const [availabilityWarning, setAvailabilityWarning] = useState<AvailabilityWarningState | null>(null);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  // Student command bar ("/") — separate, student-scoped surface; the admin
  // CommandPalette above stays admin-only.
  const [isCommandBarOpen, setIsCommandBarOpen] = useState(false);

  // Memoized safe values
  const safeUser = useMemo<SafeUser>(() => (user as SafeUser) || {
    id: 'anonymous',
    name: 'Guest User',
    email: 'guest@example.com',
    degree: 'Computer Science',
    year: 'Freshman',
  }, [user]);

  const safeAcademicPlan: AcademicPlan = academicPlan || createFallbackPlan();

  // Plan change handlers hook
  const { planOps } = usePlanChangeHandlers({
    originalPlanOps,
    academicPlan: safeAcademicPlan,
    currentPlanId,
    user,
    showSuccess,
    showError,
  });

  // Optimizer suggestion actions (generate/clear routed through chatbot)
  const {
    acceptSuggestion,
    rejectSuggestion,
    generateSuggestions,
    clearSuggestions,
    reassignGenEdArea,
    acceptingCourse: acceptingSuggestion,
    rejectingCourse: rejectingSuggestion,
    isGenerating,
  } = useOptimizerActions(currentPlanId, originalPlanOps.refreshPlan, safeAcademicPlan.optimizationStatus);

  // Optimizer mode toggle — per-session, not persisted
  // v4 (priority scheduler) is the default since v6.18.1
  const [optimizerMode, setOptimizerMode] = useState<OptimizerMode>(DEFAULT_OPTIMIZER_MODE);

  // Stale suggestions prompt — detect complete→stale transition
  const hasSuggestions = useMemo(() => {
    if (!safeAcademicPlan?.semesters) return false;
    return Object.values(safeAcademicPlan.semesters).some(
      (semCourses) => semCourses.some((c: EnrolledCourse) => c.isSuggestion),
    );
  }, [safeAcademicPlan?.semesters]);

  const {
    showStaleSuggestionsModal,
    onOptimizationStatusChange,
    dismissStaleSuggestionsModal,
  } = useStaleSuggestionsPrompt(hasSuggestions);

  // Watch optimization status changes for stale detection
  useEffect(() => {
    onOptimizationStatusChange(safeAcademicPlan.optimizationStatus);
  }, [safeAcademicPlan.optimizationStatus, onOptimizationStatusChange]);

  // App modals hook
  const {
    isAPCreditsModalOpen,
    handleOpenAPCredits,
    handleCloseAPCredits,
    handleAPCreditsSave,
    isTimelineModalOpen,
    handleCloseTimelineModal,
    handleTimelineSave,
    handleProgramsUpdated,
    showSemesterModal,
    setShowSemesterModal,
    handleManageSemesters,
    // Other Credit Modal
    isOtherCreditModalOpen,
    handleOpenOtherCredit,
    handleCloseOtherCredit,
    handleOtherCreditSubmit,
    // Add Course Modal
    isAddCourseModalOpen,
    addCourseTargetSemester,
    handleOpenAddCourse,
    handleCloseAddCourse,
    handleAddCourseFromModal,
    handleAddCoursesFromModal,
  } = useAppModals({
    user,
    dbUser,
    setDbUser,
    setSyncingUser,
    planOps,
    modalOps,
    showSuccess,
    showWarning,
    showError,
  });

  // Command execution hook — one pipeline for the chat, the student command
  // bar and the admin palette (the palette suppresses the toast).
  const { executeCommandFromChat, runCommand } = useCommandExecution({
    academicPlan: safeAcademicPlan,
    planOps,
    courses,
    courseOps,
    showSuccess,
    showError,
  });

  const executeCommandInPalette = useCallback(
    (commandString: string) => runCommand(commandString, { notify: false }),
    [runCommand]
  );

  // User update hook - called by useUserUpdate internally on user changes
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { handleUserUpdate } = useUserUpdate({
    user,
    setDbUser,
    setSyncingUser,
    planOps,
    showError,
  });

  // Availability warning for drag-and-drop: the lookup itself lives in
  // useCourseData (cached + request-coalesced + transfer-credit aware); this
  // only adds the UI concern of raising the warning banner. `is_offered` is
  // null when the check could not be resolved — that is not a "not offered"
  // answer, so it raises nothing.
  const checkCourseAvailability = useCallback(async (
    course: Pick<EnrolledCourse, 'code' | 'title'>,
    targetSemester: string,
  ): Promise<boolean> => {
    const result = await courseOps.checkCourseAvailability(course.code, targetSemester);

    if (result?.is_offered === false) {
      setAvailabilityWarning({
        courseCode: course.code,
        courseTitle: course.title,
        targetSemester,
        nextOffering: result.next_offering ?? null,
      });
    }
    return result?.is_offered !== false;
  }, [courseOps]);

  // Drag and drop: plan mutations the PlannerDndProvider's drop executor uses
  const planOpsForDrag = useMemo<PlanOpsForDrag>(
    () => ({ ...planOps, checkCourseAvailability }),
    [planOps, checkCourseAvailability]
  );

  // Cmd+Shift+O cycles the optimizer mode, announcing the new one.
  const rotateOptimizerMode = useCallback(() => {
    setOptimizerMode((prev) => {
      const next = OPTIMIZER_ROTATION[prev] || DEFAULT_OPTIMIZER_MODE;
      // Derived from the shared label map rather than a second copy, so a new
      // mode cannot be added to the type and silently miss its toast name.
      showSuccess(
        next === DEFAULT_OPTIMIZER_MODE
          ? `${OPTIMIZER_LABELS[next]} optimizer (default)`
          : `${OPTIMIZER_LABELS[next]} optimizer (${next})`,
      );
      return next;
    });
  }, [showSuccess]);

  useGlobalShortcuts({
    isAdmin,
    onOpenCommandPalette: () => setIsCommandPaletteOpen(true),
    onOpenCommandBar: () => setIsCommandBarOpen(true),
    onRotateOptimizerMode: rotateOptimizerMode,
  });

  // Direct "Suggest Courses" handler — bypasses chatbot, calls API with mode
  const handleGenerateSuggestions = useCallback(() => {
    generateSuggestions(optimizerMode);
  }, [generateSuggestions, optimizerMode]);

  // Plan switching handler
  const handlePlanSwitch = useCallback(async (planId: number) => {
    await setActivePlan(planId);
  }, [setActivePlan]);

  // Prior Credits toggle (renamed from Transfer Credits)
  const handleToggleTransferCredits = useCallback(() => {
    setShowTransferCredits((prev) => !prev);
  }, []);

  // Grade save: refreshes the plan AND the degree audit (the Cumulative GPA
  // gate lives in the audit payload). See hooks/useGradeUpdate.ts.
  const handleUpdateGrade = useGradeUpdate(currentPlanId, originalPlanOps.refreshPlan, showError);

  // Memoized context values for the planner subtree providers.
  // Hoisted here so CoursePlannerColumn stays a thin layout wrapper.
  const suggestionValue = useMemo<SuggestionContextValue>(() => ({
    onAcceptSuggestion: acceptSuggestion,
    onRejectSuggestion: rejectSuggestion,
    onReassignGenEd: reassignGenEdArea,
    acceptingSuggestion,
    rejectingSuggestion,
    isGenerating,
    optimizationStatus: safeAcademicPlan.optimizationStatus,
    optimizerMode,
    onGenerateSuggestions: handleGenerateSuggestions,
  }), [
    acceptSuggestion,
    rejectSuggestion,
    reassignGenEdArea,
    acceptingSuggestion,
    rejectingSuggestion,
    isGenerating,
    safeAcademicPlan.optimizationStatus,
    optimizerMode,
    handleGenerateSuggestions,
  ]);

  const planOpsValue = useMemo<PlanOperationsContextValue>(() => ({
    onRemoveCourse: planOps.removeCourse,
    onMoveCourse: planOps.moveCourse,
    onMoveToSemester: (course, fromSemester, fromIndex) => {
      modalOps.openSemesterSelection(course as SemesterSelectionCourse, { fromSemester, index: fromIndex });
    },
    onToggleBookmark: planOps.toggleBookmark,
    onToggleSemester: uiOps.toggleSemester,
    onClearSemester: planOps.clearSemester,
    onDeleteSemester: planOps.deleteSemester,
    onAddPlaceholder: (semester) => modalOps.openPlaceholderModal({ targetSemester: semester }),
    onEditPlaceholder: (placeholder) => modalOps.openPlaceholderModal({ editingPlaceholder: placeholder }),
    onConvertPlaceholder: (placeholder, semester, index) => {
      modalOps.openConversionModal({ placeholder, fromSemester: semester, fromIndex: index });
    },
    checkCourseAvailability: courseOps.checkCourseAvailability as PlanOperationsContextValue['checkCourseAvailability'],
    onRemoveFromBookmarks: planOps.removeFromBookmarks,
    onAddToBookmarks: planOps.addToBookmarks,
    onAddCourseFromBookmark: (course, targetSemester) => { void planOps.addCourse(course, targetSemester); },
  }), [planOps, modalOps, uiOps, courseOps]);

  const modalOpsValue = useMemo<ModalOperationsContextValue>(() => ({
    onOpenAddCourse: handleOpenAddCourse,
    onOpenAPCredits: handleOpenAPCredits,
    onAddOtherCredit: handleOpenOtherCredit,
    onManageSemesters: handleManageSemesters,
    onUpdateGrade: handleUpdateGrade,
  }), [
    handleOpenAddCourse,
    handleOpenAPCredits,
    handleOpenOtherCredit,
    handleManageSemesters,
    handleUpdateGrade,
  ]);

  // Shared layout values, used by both the initial-load shell and the real
  // layout below so the skeleton lines up with the real columns (no jump).
  const mainHeightStyle = {
    height: showTabBar
      ? 'calc(100vh - 64px - 64px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))'
      : 'calc(100vh - 64px - env(safe-area-inset-top, 0px))',
  };
  // UI Tier 3.3a: desktop columns are user-resizable via drag dividers
  // (fractions persisted); tablet/mobile keep the fixed grid.
  const { gridStyle, getSeparatorProps, isResizing } = useResizableColumns(chatCollapsed ? 2 : 3);
  // W49 A3: institution graduation target for the print header denominator.
  const { config: institutionConfig } = useInstitutionConfig();
  const gridClassName = `grid h-full ${
    isDesktop
      ? `${isResizing ? 'select-none cursor-col-resize' : ''}`
      : isTablet
        ? 'grid-cols-2 gap-4'
        : 'grid-cols-1 gap-4'
  }`;
  const gridInlineStyle = isDesktop ? gridStyle : undefined;

  // Initial load — render the shell (Header + skeleton columns) immediately
  // instead of a blank full-page spinner, so the app appears instantly with
  // structure. Once currentPlanId resolves we render the real layout below and
  // each column fills in its own content progressively (each self-fetches).
  const isInitialLoad = !currentPlanId;
  if ((isInitialLoad && (planLoading || plansLoading)) || syncingUser) {
    return (
      <div className="h-screen bg-surface-ground flex flex-col overflow-hidden">
        <Header
          studentProfile={dbUser || safeUser}
          planSwitcher={<div className="h-9 w-40 bg-gray-200 rounded-lg animate-pulse" aria-hidden="true" />}
          onOpenCommandCenter={() => setIsCommandPaletteOpen(true)}
        />
        <main className="flex-1" style={mainHeightStyle}>
          <AppLoadingShell
            gridClassName={`grid gap-4 lg:gap-6 h-full ${
              isDesktop ? 'grid-cols-[1fr_1fr_1.3fr]' : isTablet ? 'grid-cols-2' : 'grid-cols-1'
            }`}
            message={syncingUser ? 'Setting up your profile…' : 'Loading your academic plan…'}
          />
        </main>
      </div>
    );
  }

  // Error state
  if (planError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-ground">
        <ErrorState
          type="server"
          title="Failed to load academic plan"
          message={planError}
          onRetry={() => window.location.reload()}
          size="large"
        />
      </div>
    );
  }

  return (
    <div className="h-screen bg-surface-ground flex flex-col overflow-hidden">
      <Header
        studentProfile={dbUser || safeUser}
        planSwitcher={<PlanSwitcher onPlanChange={handlePlanSwitch} onRefreshPlan={originalPlanOps.refreshPlan} />}
        onOpenCommandCenter={() => setIsCommandPaletteOpen(true)}
        planId={currentPlanId}
      />

      {/* Print Header - appears at top of printed document */}
      <PrintHeader
        student={dbUser}
        fallbackStudent={safeUser}
        academicPlan={safeAcademicPlan}
        graduationCredits={institutionConfig.graduationCredits}
      />

      {saving && (
        <div
          role="status"
          aria-live="polite"
          className="fixed top-20 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50"
        >
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" aria-hidden="true"></div>
          <span>Saving...</span>
        </div>
      )}

      <main className="flex-1" style={mainHeightStyle}>
        <div className="h-full px-4 sm:px-6 lg:px-8 py-4 lg:py-8 box-border">
          <div className={gridClassName} style={gridInlineStyle}>

            {visibleColumns.audit && (
            <SectionErrorBoundary
              title="Degree Audit Error"
              message="Unable to load your degree requirements. Please refresh the page or try again later."
            >
              <DegreeAuditColumn
                hasAcademicPlan={!!activePlan}
                academicPlan={{ ...safeAcademicPlan, id: currentPlanId ?? safeAcademicPlan.id }}
                onAddToSemester={(course) => modalOps.openSemesterSelection(course as SemesterSelectionCourse)}
                onToggleBookmark={planOps.toggleBookmark}
                onAddCourseToPlan={(course, semester) => { void planOps.addCourse(course, semester); }}
                onChangeRequirement={(course) => {
                  const c = course as { code: string };
                  modalOps.openRequirementModal({
                    course: c as SemesterSelectionCourse,
                    currentRequirements: courseRequirements[c.code] || [],
                  });
                }}
                courseRequirements={courseRequirements}
                openDropdownId={openDropdownId}
                setOpenDropdownId={uiOps.openDropdown}
                chatDebugInfo={chatDebugInfo}
                onManageSemesters={handleManageSemesters}
                onDismissSetup={() => localStorage.setItem('academic_planner_setup_dismissed', 'true')}
              />
            </SectionErrorBoundary>
            )}

            {isDesktop && (
              <div
                {...getSeparatorProps(0)}
                className="group flex items-center justify-center w-3 -mx-1 cursor-col-resize select-none focus-visible:outline-none"
              >
                <div className="w-1 h-24 rounded-full bg-line-soft group-hover:bg-ink-3/60 group-hover:h-32 group-focus-visible:bg-primary transition-all duration-fast" />
              </div>
            )}

            {visibleColumns.planner && (
            <SectionErrorBoundary
              title="Course Planner Error"
              message="Unable to display your course plan. Your data is safe - please refresh to try again."
            >
              <PlannerDndProvider planOps={planOpsForDrag}>
                <SuggestionProvider value={suggestionValue}>
                  <PlanOperationsProvider value={planOpsValue}>
                    <ModalOperationsProvider value={modalOpsValue}>
                      <CoursePlannerColumn
                        academicPlan={safeAcademicPlan}
                        loading={isInitialLoad && planLoading}
                        refreshing={planRefreshing || (!isInitialLoad && planLoading)}
                        collapsedSemesters={collapsedSemesters}
                        bookmarkedCollapsed={bookmarkedCollapsed}
                        showBookmarkedCourses={showBookmarkedCourses}
                        onToggleBookmarks={uiOps.toggleBookmarks}
                        openDropdownId={openDropdownId}
                        setOpenDropdownId={uiOps.openDropdown}
                        onApplyTemplate={planOps.applyTemplate}
                        onClearAllCourses={planOps.clearAllCourses}
                        showTransferCredits={showTransferCredits}
                        onToggleTransferCredits={handleToggleTransferCredits}
                      />
                    </ModalOperationsProvider>
                  </PlanOperationsProvider>
                </SuggestionProvider>
              </PlannerDndProvider>
            </SectionErrorBoundary>
            )}

            {isDesktop && !chatCollapsed && (
              <div
                {...getSeparatorProps(1)}
                className="group flex items-center justify-center w-3 -mx-1 cursor-col-resize select-none focus-visible:outline-none"
              >
                <div className="w-1 h-24 rounded-full bg-line-soft group-hover:bg-ink-3/60 group-hover:h-32 group-focus-visible:bg-primary transition-all duration-fast" />
              </div>
            )}

            {visibleColumns.chat && (
            <div className={chatCollapsed ? 'hidden' : 'contents'}>
            <SectionErrorBoundary
              title="Chat Error"
              message="Unable to load the AI assistant. Please check your connection and try again."
            >
              <ChatColumn
                academicPlan={safeAcademicPlan}
                academicPlanId={currentPlanId}
                userId={user?.id}
                updateChatDebugInfo={setChatDebugInfo}
                onExecuteCommand={executeCommandFromChat}
                onPlanUpdate={originalPlanOps.refreshPlan}
                onClearSuggestionsLocally={originalPlanOps.clearSuggestionsLocally}
                onMergeSuggestionsLocally={originalPlanOps.mergeSuggestionsLocally}
              />
            </SectionErrorBoundary>
            </div>
            )}
          </div>
        </div>
      </main>

      {/* Mobile Tab Bar */}
      <MobileTabBar />

      {/* UX-R1 3.1: floating re-engagement pill while the assistant is
          disabled on desktop (quick actions + enable). */}
      {chatCollapsed && visibleColumns.chat && <ChatMinimizedPill />}

      {/* Modals */}
      <ModalManager
        modals={modals}
        modalData={modalData}
        modalOps={modalOps}
        planOps={planOps}
        planUtils={planUtils}
        courses={courses}
        courseOps={courseOps}
        requirements={courseRequirements}
        currentPlanId={currentPlanId}
        showSemesterModal={showSemesterModal}
        setShowSemesterModal={setShowSemesterModal}
        academicPlan={safeAcademicPlan}
        onProgramsUpdated={handleProgramsUpdated}
        onOpenAPCredits={handleOpenAPCredits}
        onAddOtherCredit={handleOpenOtherCredit}
        onOpenBookmarkedCourses={uiOps.expandBookmarks}
        onSetShowBookmarkedCourses={uiOps.setShowBookmarkedCourses}
        showBookmarkedCourses={showBookmarkedCourses}
        // Other Credit Modal
        isOtherCreditModalOpen={isOtherCreditModalOpen}
        onCloseOtherCredit={handleCloseOtherCredit}
        onOtherCreditSubmit={handleOtherCreditSubmit}
        // Add Course Modal
        isAddCourseModalOpen={isAddCourseModalOpen}
        onCloseAddCourse={handleCloseAddCourse}
        onAddCourseFromModal={handleAddCourseFromModal}
        onAddCoursesFromModal={handleAddCoursesFromModal}
        addCourseTargetSemester={addCourseTargetSemester}
        // Enrollment Settings Modal
        isTimelineModalOpen={isTimelineModalOpen}
        onCloseTimeline={handleCloseTimelineModal}
        dbUser={dbUser}
        onTimelineSave={handleTimelineSave}
        onResetPlan={async () => {
          if (planOps && planOps.refreshPlan) {
            await planOps.refreshPlan();
          }
        }}
      />

      <APCreditsModal
        isOpen={isAPCreditsModalOpen}
        onClose={handleCloseAPCredits}
        userId={user?.id}
        onSave={handleAPCreditsSave}
        onRefreshPlan={originalPlanOps.refreshPlan}
        showSuccess={showSuccess}
        showError={showError}
        showWarning={showWarning}
      />

      <StaleSuggestionsModal
        isOpen={showStaleSuggestionsModal}
        onClose={dismissStaleSuggestionsModal}
        onRecalculate={() => {
          dismissStaleSuggestionsModal();
          generateSuggestions(optimizerMode);
        }}
        onClearSuggestions={() => {
          dismissStaleSuggestionsModal();
          clearSuggestions();
        }}
        onKeep={dismissStaleSuggestionsModal}
        isGenerating={isGenerating}
      />

      <OnboardingModal
        isOpen={showOnboarding}
        onClose={dismissOnboarding}
        mode={onboardingMode}
        onRequestAPCredits={handleOpenAPCredits}
        onComplete={async () => {
          completeOnboarding();
          // Refresh plan data after onboarding
          if (originalPlanOps?.refreshPlan) {
            await originalPlanOps.refreshPlan();
          }
        }}
      />

      <StudentCommandBar
        isOpen={isCommandBarOpen}
        onClose={() => setIsCommandBarOpen(false)}
        onExecute={executeCommandFromChat}
      />

      {/* The palette prints results into its own terminal, so it runs
          commands without the toast the chat / command-bar path uses. */}
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
        onExecute={executeCommandInPalette}
      />

      <AvailabilityWarning
        warning={availabilityWarning}
        onClose={() => setAvailabilityWarning(null)}
      />

      {toasts.map((toast) => (
        <ToastNotification
          key={toast.id}
          show={true}
          type={toast.type}
          title={toast.title}
          message={toast.message}
          subMessage={toast.subMessage}
          autoDismiss={toast.autoDismiss}
          duration={toast.duration}
          onClose={() => hideToast(toast.id)}
        />
      ))}
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary
      onReset={() => {
        localStorage.removeItem('academic_planner_db_user');
        window.location.reload();
      }}
    >
      <AppProviders>
        <AppRoutes>
          <AppContent />
        </AppRoutes>
      </AppProviders>
    </ErrorBoundary>
  );
}

export default App;
